import type { KpiOption } from '../config/kpiCatalog';
import type { PlanMetricDimensionDefault, PlanMetricKpiDefault, PromoMetricKpiDefault } from '../../../config/metricPageConfig';

/**
 * Resolves the default dimension for a given subMode from the per-subMode
 * config array. Falls back to `fallback` if no entry matches.
 */
export function resolvePlanMetricDimension(
    defaults: PlanMetricDimensionDefault[],
    subMode: string,
    fallback: string,
): string {
    return defaults.find((d) => d.subMode === subMode)?.value ?? fallback;
}

/**
 * Resolves default KPI keys from an entry that has either `value` (explicit)
 * or `resolveFromField` (dynamic lookup against `kpi.meta`).
 */
function resolveKpiEntry(
    entry: { value?: string[]; resolveFromField?: string },
    kpiCatalog: readonly KpiOption[],
    fallbackKpi: string,
): string[] {
    if (entry.value) return entry.value;

    if (entry.resolveFromField) {
        const field = entry.resolveFromField;
        const resolved = kpiCatalog.filter((k) => k.meta[field] === true).map((k) => k.key);
        if (resolved.length > 0) return resolved;
    }

    return [fallbackKpi];
}

/**
 * Resolves the default Plan Metric KPI keys for a given subMode.
 * Falls back to `[fallbackKpi]` if no entry matches or resolution yields
 * an empty set.
 */
export function resolvePlanMetricKpis(
    defaults: PlanMetricKpiDefault[],
    subMode: string,
    kpiCatalog: readonly KpiOption[],
    fallbackKpi: string,
): string[] {
    const entry = defaults.find((d) => d.subMode === subMode);
    if (!entry) return [fallbackKpi];
    return resolveKpiEntry(entry, kpiCatalog, fallbackKpi);
}

/**
 * Resolves the default Promo Metric KPI keys.
 * Promo Metrics has no subMode — it's a flat resolution from either explicit
 * `value` or a `resolveFromField` lookup against the KPI catalog.
 */
export function resolvePromoMetricKpis(
    config: PromoMetricKpiDefault,
    kpiCatalog: readonly KpiOption[],
    fallbackKpi: string,
): string[] {
    return resolveKpiEntry(config, kpiCatalog, fallbackKpi);
}
